import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { AppropriatenessTestOvernightLossReaction } from '@/features/futures/schemas/appropriateness-test';
import { usePromise } from '@/hooks/use-promise';
import { FUTURES_APPROPRIATENESS_OVERNIGHT_LOSS_REACTION_ANSWERS } from '@/services/api/profile/futures-appropriateness';

const FuturesAppropriatenessTestFieldsetOvernightLossReactionLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.overnightLossReaction.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.overnightLossReaction.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
FuturesAppropriatenessTestFieldsetOvernightLossReactionLegend.displayName = 'FuturesAppropriatenessTestFieldsetOvernightLossReactionLegend';

const FuturesAppropriatenessTestFieldsetOvernightLossReaction = () => {
  const form = useFormContext<Partial<AppropriatenessTestOvernightLossReaction>>();
  const { nextStepDeferred } = useGuardedFormStepper();
  const { isPending, handler: handleNextStep } = usePromise(() => nextStepDeferred({ submit: true }));
  const { t } = useTranslation();

  return (
    <FormField
      name="overnightLossReaction"
      control={form.control}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="sr-only">{t('FuturesAppropriatenessAssessment.overnightLossReaction.title')}</FormLabel>
          <FormControl>
            <RadioGroup
              className="!mt-0"
              defaultValue={field.value}
              variant="cards"
              aria-label={t('FuturesAppropriatenessAssessment.overnightLossReaction.title')}
              onValueChange={(value) => {
                field.onChange(value);
                handleNextStep();
              }}
            >
              {FUTURES_APPROPRIATENESS_OVERNIGHT_LOSS_REACTION_ANSWERS.map((answer) => (
                <FormItem key={answer}>
                  <RadioGroupItemCard variant="outline">
                    <FormControl>
                      <RadioGroupItem value={answer} aria-disabled={isPending} />
                    </FormControl>
                    <FormLabel contained onClick={handleNextStep}>
                      {t(`FuturesAppropriatenessAssessment.overnightLossReaction.options.${answer}`)}
                    </FormLabel>
                  </RadioGroupItemCard>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FuturesAppropriatenessTestFieldsetOvernightLossReaction.displayName = 'FuturesAppropriatenessTestFieldsetOvernightLossReaction';
FuturesAppropriatenessTestFieldsetOvernightLossReaction.isAutoNextStep = true;

export { FuturesAppropriatenessTestFieldsetOvernightLossReaction, FuturesAppropriatenessTestFieldsetOvernightLossReactionLegend };
